import { initReactI18next } from "react-i18next";

import OtaClient from "@crowdin/ota-client";
import i18n, { InitOptions } from "i18next";
import ChainedBackend from "i18next-chained-backend";
import HttpBackend, { HttpBackendOptions, RequestCallback } from "i18next-http-backend";
import LocalStorageBackend, { LocalStorageBackendOptions } from "i18next-localstorage-backend";

import { createBundledTranslationFallback, createMissingTranslationReporter, getLanguageFromLocalStorage } from "utils";

import { LANGUAGE_CODE } from "consts";

const DISTRIBUTION_HASH = process.env.REACT_APP_CROWDIN_DISTRIBUTION_HASH || "";
const REACT_APP_CROWDIN_DISTRIBUTION_MANIFEST = `https://distributions.crowdin.net/${DISTRIBUTION_HASH}`;
const client = new OtaClient(DISTRIBUTION_HASH);

const I18N_CACHE_PREFIX = "i18next_res_";
const I18N_MANIFEST_TIMESTAMP_KEY = "i18n_manifest_timestamp";

type XHRRequestOptions = {
  crossDomain?: boolean;
  withCredentials?: boolean;
  customHeaders?: Record<string, string> | (() => Record<string, string>);
};

const requestWithXmlHttpRequest = (options: XHRRequestOptions, url: string, payload: object | string | undefined, callback: RequestCallback): void => {
  try {
    const x = new XMLHttpRequest();
    x.open("GET", url, true);
    if (!options.crossDomain) {
      x.setRequestHeader("X-Requested-With", "XMLHttpRequest");
    }
    x.withCredentials = !!options.withCredentials;
    if (payload) {
      x.setRequestHeader("Content-Type", "application/x-www-form-urlencoded");
    }
    if (x.overrideMimeType) {
      x.overrideMimeType("application/json");
    }
    const h = options.customHeaders;
    const headers = typeof h === "function" ? h() : h;
    if (headers) {
      for (const i in headers) {
        x.setRequestHeader(i, headers[i]);
      }
    }
    x.onreadystatechange = () => {
      if (x.readyState > 3) {
        callback(x.status >= 400 ? x.statusText : null, {
          status: x.status || 200,
          data: x.responseText,
        });
      }
    };
    x.send(payload as string | undefined);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }
};

// Create a custom backend class for backup
class BackupHttpBackend extends HttpBackend {
  constructor(services?: ConstructorParameters<typeof HttpBackend>[0], options?: ConstructorParameters<typeof HttpBackend>[1]) {
    super(services, { ...options });
  }
}

const formatLanguageCodeForCrowdin = (lngs: string | readonly string[]): string => {
  // Fix language casing for Crowdin OTA: i18next lowercases 'sk-SK' to 'sk-sk' by default,
  // but Crowdin requires proper casing ('sk-SK') in the URL path to load the correct files.
  const lng = Array.isArray(lngs) ? lngs[0] : lngs;
  return lng.replace(/([a-z]{2})-([a-z]{2})/, (_match: string, lang: string, region: string) => `${lang}-${region.toUpperCase()}`);
};

const clearTranslationCache = (): void => {
  const keysToRemove = Object.keys(localStorage).filter((key) => key.startsWith(I18N_CACHE_PREFIX));
  keysToRemove.forEach((key) => localStorage.removeItem(key));
  if (keysToRemove.length > 0) {
    // eslint-disable-next-line no-console
    console.log(`Cleared ${keysToRemove.length} cached translation entries`);
  }
};

const invalidateTranslationCacheIfStale = async (manifest: { timestamp: number; files: string[] }): Promise<void> => {
  const currentTimestamp = String(manifest.timestamp);
  const storedTimestamp = localStorage.getItem(I18N_MANIFEST_TIMESTAMP_KEY);

  if (storedTimestamp !== currentTimestamp) {
    // eslint-disable-next-line no-console
    console.log(`Crowdin distribution updated (${storedTimestamp} → ${currentTimestamp}). Clearing translation cache.`);
    clearTranslationCache();
    localStorage.setItem(I18N_MANIFEST_TIMESTAMP_KEY, currentTimestamp);
  }
};

const getNamespaces = async (): Promise<string[]> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const manifest = await (client as any).manifest;

  // Check if distribution has been updated and clear stale cache
  await invalidateTranslationCacheIfStale(manifest);

  // manifest.files is an array of all namespace files in our distribution
  const namespaces = manifest.files.map(
    (file: string) =>
      // Strip folder + extension → e.g. "aboutUs.json" → "aboutUs"
      file.split("/").pop()?.replace(".json", "")
  );
  return Array.from(new Set(namespaces)) as string[];
};

const localStorageOptions: LocalStorageBackendOptions = {
  // options for caching
  prefix: "i18next_res_",
  expirationTime: 24 * 60 * 60 * 1000,
};

const httpBackendOptions: HttpBackendOptions = {
  // options for OTA
  loadPath: (lngs: string | readonly string[]) => {
    const lng = formatLanguageCodeForCrowdin(lngs);
    // Use backup for Zulu language since it does not exist on crowdin
    if (lng === "zu") {
      return `${process.env.REACT_APP_TRANSLATIONS || ""}/translations/${lng}/{{ns}}.json`;
    }
    // This language is only used for in-context-localization for crowdin
    if (lng === "ach") {
      return `${process.env.REACT_APP_TRANSLATIONS || ""}/translations/ach-UG.json`;
    }
    return `${REACT_APP_CROWDIN_DISTRIBUTION_MANIFEST}/content/public/translations/${lng}/{{ns}}.json`;
  },
};

const backupHttpBackendOptions: HttpBackendOptions = {
  // options for Backup
  loadPath: (lngs: string | readonly string[]) => {
    const lng = formatLanguageCodeForCrowdin(lngs);
    return `${process.env.REACT_APP_TRANSLATIONS || ""}/translations/${lng}/{{ns}}.json`;
  },
  request: requestWithXmlHttpRequest,
};

const FILE_NOT_FOUND_STATUS = 404;

// Resolves null when the shipped file does not exist, rejects when it could not be loaded (offline, server error), so the load is tried again later.
const loadBundledTranslationFile = (url: string): Promise<Record<string, unknown> | null> =>
  new Promise((resolve, reject) => {
    requestWithXmlHttpRequest({}, url, undefined, (error, response) => {
      if (response?.status === FILE_NOT_FOUND_STATUS) {
        resolve(null);
        return;
      }
      // requestWithXmlHttpRequest reports a request without answer (offline) as an empty successful response
      if (error || !response || response.data === "") {
        reject(new Error(`Could not load ${url}`));
        return;
      }
      try {
        resolve(typeof response.data === "string" ? JSON.parse(response.data) : response.data);
      } catch {
        resolve(null);
      }
    });
  });

// Missing keys in the Crowdin texts (for example after a rollback) are filled from the translation files shipped with this build.
const loadMissingKeysFromBuild = createBundledTranslationFallback({
  i18nInstance: i18n,
  loadBundle: loadBundledTranslationFile,
  translationsBaseUrl: process.env.REACT_APP_TRANSLATIONS || "",
  toFileLanguageCode: formatLanguageCodeForCrowdin,
});

const reportMissingTranslation = createMissingTranslationReporter({
  isTextAvailable: (namespace, key) => i18n.exists(key, { ns: namespace }),
});

const getI18nConfig = (namespaces: string[] = []): InitOptions => ({
  lng: getLanguageFromLocalStorage(),
  backend: {
    // Please do not change the order of 'backends' below.
    backends: [
      LocalStorageBackend, // Caching settings
      HttpBackend, // 1. Try OTA
      BackupHttpBackend, // 2. Fallback to local
    ],
    backendOptions: [localStorageOptions, httpBackendOptions, backupHttpBackendOptions],
  },
  lowerCaseLng: true,
  load: "all",
  fallbackLng: LANGUAGE_CODE.GERMAN,
  fallbackNS: "global",
  debug: false,
  // only reports missing keys to missingKeyHandler, nothing is sent to a server
  saveMissing: true,
  saveMissingTo: "all",
  missingKeyHandler: (languages, namespace, key) => {
    if (!i18n.hasLoadedNamespace(namespace)) return;
    loadMissingKeysFromBuild(languages, namespace).then(() => reportMissingTranslation(languages[0], namespace, key));
  },
  defaultNS: "nav",
  ns: namespaces?.length > 0 ? namespaces : ["global", "nav"],
  interpolation: {
    escapeValue: false,
    formatSeparator: ",",
  },
  react: {
    useSuspense: true,
    // re-render when texts from the shipped files are added
    bindI18nStore: "added",
  },
});

// Initialize i18n with all name spaces
const initializeI18n = async (): Promise<void> => {
  let allNameSpaces: string[] = [];

  try {
    allNameSpaces = await getNamespaces();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to load namespaces from Crowdin:", error);
  }

  await i18n
    .use(ChainedBackend)
    .use(initReactI18next)
    .init(getI18nConfig(allNameSpaces), (_err) => {
      // eslint-disable-next-line no-console
      if (_err) return console.error(_err);
      // eslint-disable-next-line no-console
      if (!process.env.JEST_WORKER_ID) console.log("Loading translation has been completed.");
    });
};

await initializeI18n();

export default i18n;
